import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faSpinner } from '@fortawesome/pro-light-svg-icons/faSpinner';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { smartLinkPlatformDomains } from 'fym-shared/src/smartlinks/domains';
import { TypedMemo } from '../../common/types';
import { Panel, PanelBody, PanelHead } from '../../music24/components/primitives/Panel';
import { AccountSlugCheck, AddressState } from './useAccountSlugCheck';
import { ProfileAddressImpact } from './ProfileAddressImpact';
import { accountSlugRefusalText } from './accountSlugRefusalText';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole profile editor; each part uses its own slice of it */
import styles from './SMLProfile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileAddressCardProps {
  accountId: number;
  slug: string;
  platformDomain: string;
  savedSlug: string;
  savedPlatformDomain: string;
  isAddressDirty: boolean;
  check: AccountSlugCheck;
  onSlugChange: (slug: string) => void;
  onPlatformDomainChange: (platformDomain: string) => void;
  disabled?: boolean;
}

/**
 * The whole address in one control.
 *
 * The old screen split it: a field called "Slug" on one side of the grid and
 * three unlabelled chips for the host on the other, with nothing showing what
 * the two made together. Here the artist reads the address left to right and
 * the availability check runs while they type.
 */
const ProfileAddressCardComponent: React.FC<ProfileAddressCardProps> = ({
  accountId,
  slug,
  platformDomain,
  savedSlug,
  savedPlatformDomain,
  isAddressDirty,
  check,
  onSlugChange,
  onPlatformDomainChange,
  disabled,
}) => {
  const intl = useIntl();
  const [isPickerOpen, setPickerOpen] = useState(false);

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onSlugChange(event.target.value),
    [onSlugChange]
  );

  const togglePicker = useCallback(() => setPickerOpen((open) => !open), []);

  const domains = useMemo(() => [...smartLinkPlatformDomains], []);

  const isBad = check.state === AddressState.taken || check.state === AddressState.illegal;

  const addressLabel = intl.formatMessage({ id: 'app.sml.profile.address.label', defaultMessage: 'Your address' });

  const takeSuggestion = useCallback(() => {
    if (check.suggestion !== undefined) {
      onSlugChange(check.suggestion);
    }
  }, [check.suggestion, onSlugChange]);

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faLink} />}
        title={intl.formatMessage({ id: 'app.sml.profile.address.title', defaultMessage: 'Page address' })}
        right={
          <span className={styles.headNote}>
            {intl.formatMessage({
              id: 'app.sml.profile.address.note',
              defaultMessage: 'used by every smartlink',
            })}
          </span>
        }
      />
      <PanelBody stack>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="sml-profile-slug">
            {addressLabel}
          </label>
          <div className={clsx(styles.address, isBad && styles.addressBad)}>
            <span className={styles.addressScheme}>https://</span>
            <span className={styles.addressSlug}>
              <input
                id="sml-profile-slug"
                className={styles.addressInput}
                value={slug}
                disabled={disabled}
                spellCheck={false}
                onChange={onInputChange}
                aria-label={addressLabel}
              />
            </span>
            <button
              type="button"
              className={styles.addressDomain}
              disabled={disabled}
              onClick={togglePicker}
              aria-expanded={isPickerOpen}
            >
              .{platformDomain}
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
            <span className={styles.addressStatus}>
              {check.state === AddressState.checking && (
                <FontAwesomeIcon icon={faSpinner} spin className={styles.statusChecking} />
              )}
              {check.state === AddressState.free && <FontAwesomeIcon icon={faCheck} className={styles.statusFree} />}
              {isBad && <FontAwesomeIcon icon={faXmark} className={styles.statusBad} />}
            </span>
          </div>
          <span className={clsx(styles.fieldHelp, isBad && styles.fieldHelpBad)}>
            {check.state === AddressState.taken &&
              intl.formatMessage({
                id: 'app.sml.profile.address.taken',
                defaultMessage: 'Taken by another artist. Addresses are first come, first served.',
              })}
            {check.state === AddressState.illegal &&
              (check.refusal === null
                ? intl.formatMessage({
                    id: 'app.sml.profile.address.illegal',
                    defaultMessage: 'An address takes letters, numbers and dashes.',
                  })
                : accountSlugRefusalText(intl, check.refusal))}
            {!isBad &&
              intl.formatMessage(
                {
                  id: 'app.sml.profile.address.help',
                  defaultMessage: 'Every page you make hangs off this: {example}',
                },
                {
                  // A rich value comes back in an array: it needs a key (QA lit-22).
                  example: (
                    <span key="example" className={styles.fieldStrong}>
                      {slug === ''
                        ? intl.formatMessage({ id: 'app.sml.profile.address.example.name', defaultMessage: 'yourname' })
                        : slug}
                      .{platformDomain}/
                      {intl.formatMessage({
                        id: 'app.sml.profile.address.example.page',
                        defaultMessage: 'your-release',
                      })}
                    </span>
                  ),
                }
              )}
          </span>
        </div>

        {check.suggestion !== undefined && isBad && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              {intl.formatMessage({ id: 'app.sml.profile.address.free-now', defaultMessage: 'Free right now' })}
            </span>
            <div className={styles.chipRow}>
              <button type="button" className={clsx(styles.chip, styles.chipMono)} onClick={takeSuggestion}>
                {check.suggestion}
              </button>
            </div>
          </div>
        )}

        {isPickerOpen && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              {intl.formatMessage({ id: 'app.sml.profile.domain.label', defaultMessage: 'Domain' })}
            </span>
            <div className={styles.domainList}>
              {domains.map((domain) => (
                <DomainRow
                  key={domain}
                  domain={domain}
                  isSelected={domain === platformDomain}
                  onSelect={onPlatformDomainChange}
                  disabled={disabled}
                />
              ))}
            </div>
            <span className={styles.fieldHelp}>
              {intl.formatMessage({
                id: 'app.sml.profile.domain.help',
                defaultMessage:
                  'All of them open the same page, so an address you have already shared goes on working.',
              })}
            </span>
          </div>
        )}

        {isAddressDirty && (
          <ProfileAddressImpact
            accountId={accountId}
            savedSlug={savedSlug}
            savedPlatformDomain={savedPlatformDomain}
            slug={slug}
            platformDomain={platformDomain}
          />
        )}
      </PanelBody>
    </Panel>
  );
};

interface DomainRowProps {
  domain: string;
  isSelected: boolean;
  onSelect: (domain: string) => void;
  disabled?: boolean;
}

/**
 * One host, named and nothing more.
 *
 * There is nothing to say about a host that is true of only that host — they
 * all serve the same page — so the row carries the name and the note under the
 * list carries the one fact that matters.
 */
const DomainRowComponent: React.FC<DomainRowProps> = ({ domain, isSelected, onSelect, disabled }) => {
  const select = useCallback(() => onSelect(domain), [domain, onSelect]);
  return (
    <button
      type="button"
      className={clsx(styles.domainRow, isSelected && styles.domainRowOn)}
      onClick={select}
      disabled={disabled}
      aria-pressed={isSelected}
    >
      <span className={styles.domainRadio} />
      <span className={styles.domainName}>{domain}</span>
    </button>
  );
};

const DomainRow = TypedMemo(DomainRowComponent);

export const ProfileAddressCard = TypedMemo(ProfileAddressCardComponent);
